/**
 * InlineNewItemPopover — Iter 240
 *
 * Compact popover with a mini form to create a new catalog item without
 * leaving the RFQ/Estimate grid. Reports the created item back to the
 * caller so the row can auto-fill.
 */
import { useState } from "react";
import { Plus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

export default function InlineNewItemPopover({ wsId, categories = [], onCreated,
                                                testid = "inline-new-item" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", category: "", unit: "unit", rate: 0,
    effort_hours: "", tax_pct: 0, hsn_sac: "",
  });

  const reset = () => setForm({
    name: "", category: "", unit: "unit", rate: 0,
    effort_hours: "", tax_pct: 0, hsn_sac: "",
  });

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category || undefined,
        unit: form.unit || "unit",
        rate: Number(form.rate) || 0,
        effort_hours: form.effort_hours === "" ? null : Number(form.effort_hours),
        tax_pct: Number(form.tax_pct) || 0,
        hsn_sac: form.hsn_sac || undefined,
      };
      const { data } = await api.post(`/workspaces/${wsId}/items`, payload);
      toast.success(`Added "${data.name}" to catalog`);
      onCreated?.(data);
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to add item"));
    } finally { setBusy(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button"
          className="w-6 h-6 shrink-0 border border-border text-muted-foreground hover:bg-muted hover:text-foreground flex items-center justify-center"
          title="Add new item to catalog"
          data-testid={testid}>
          <Plus className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" side="right" align="start" data-testid={`${testid}-panel`}>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          New catalog item
        </div>
        <form onSubmit={submit} className="space-y-2">
          <input required autoFocus
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Item name *"
            className="w-full text-xs px-2 py-1.5 bg-background border border-border focus:border-primary focus:outline-none"
            data-testid={`${testid}-name`} />
          <div className="grid grid-cols-2 gap-2">
            <input value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              list={`${testid}-cat-list`}
              placeholder="Category"
              className="text-xs px-2 py-1.5 bg-background border border-border focus:border-primary focus:outline-none"
              data-testid={`${testid}-category`} />
            <input value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              placeholder="Unit"
              className="text-xs px-2 py-1.5 bg-background border border-border focus:border-primary focus:outline-none"
              data-testid={`${testid}-unit`} />
            <datalist id={`${testid}-cat-list`}>
              {categories.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[9px] uppercase text-muted-foreground">Rate</label>
              <input type="number" step="1" value={form.rate}
                onChange={(e) => setForm({ ...form, rate: e.target.value })}
                className="w-full text-xs px-2 py-1 bg-background border border-border tabular-nums text-right focus:border-primary focus:outline-none"
                data-testid={`${testid}-rate`} />
            </div>
            <div>
              <label className="text-[9px] uppercase text-muted-foreground">Effort (h)</label>
              <input type="number" step="0.5" value={form.effort_hours}
                onChange={(e) => setForm({ ...form, effort_hours: e.target.value })}
                className="w-full text-xs px-2 py-1 bg-background border border-border tabular-nums text-right focus:border-primary focus:outline-none"
                data-testid={`${testid}-effort`} />
            </div>
            <div>
              <label className="text-[9px] uppercase text-muted-foreground">Tax %</label>
              <input type="number" step="0.5" value={form.tax_pct}
                onChange={(e) => setForm({ ...form, tax_pct: e.target.value })}
                className="w-full text-xs px-2 py-1 bg-background border border-border tabular-nums text-right focus:border-primary focus:outline-none"
                data-testid={`${testid}-tax`} />
            </div>
          </div>
          <input value={form.hsn_sac}
            onChange={(e) => setForm({ ...form, hsn_sac: e.target.value })}
            placeholder="HSN / SAC"
            className="w-full text-xs px-2 py-1.5 bg-background border border-border font-mono focus:border-primary focus:outline-none"
            data-testid={`${testid}-hsn`} />
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" size="sm"
              onClick={() => { setOpen(false); reset(); }}
              data-testid={`${testid}-cancel`}>Cancel</Button>
            <Button type="submit" size="sm" disabled={busy}
              data-testid={`${testid}-submit`}>
              {busy ? "Adding…" : "Add & attach"}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
